import React from 'react';
import {
  HStack, Button, ButtonIcon, ButtonText, Badge, BadgeText, Box, Icon, Pressable,
} from '@gluestack-ui/themed';
import { Sparkles, RefreshCw } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import SubmitButton from './SubmitButton';

export interface EditorToolbarProps {
  disableButtons?: boolean;
  onEvaluate?: () => void | Promise<void>;
  onSubmit?: () => void | Promise<void>;
  canSubmit?: boolean;
  showEvaluate?: boolean;
  /** False while the draft is empty: there is nothing to give feedback on yet. */
  canEvaluate?: boolean;
  showSubmitButton?: boolean;
  showWordcountInline?: boolean;
  wordcount?: { bodyWords: number; allWords?: number };
  targetWordcount?: number;
  wordcountColor?: string;
  compactSaveLabel?: string;
  compactSaveColor?: string;
  onRetry?: () => void;
  /** Own row above the editor instead of overlaying its toolbar; phones lack room for both. */
  inline?: boolean;
}

const EditorToolbarComponent: React.FC<EditorToolbarProps> = ({
  disableButtons,
  onEvaluate,
  canSubmit,
  onSubmit,
  showEvaluate,
  canEvaluate = true,
  showSubmitButton,
  showWordcountInline,
  wordcount,
  targetWordcount,
  wordcountColor,
  compactSaveLabel,
  compactSaveColor,
  onRetry,
  inline = false,
}) => {
  const { t } = useTranslation();
  return (
    <HStack
      testID="editor-toolbar"
      space="sm"
      justifyContent="flex-end"
      alignItems="center"
      position={inline ? 'relative' : 'absolute'}
      top={inline ? undefined : 2}
      right={inline ? undefined : 2}
      zIndex={5}
      px={inline ? '$0' : '$2'}
      mb={inline ? '$2' : undefined}
      h={40}
    >
      <HStack space="sm" alignItems="center">
        {showWordcountInline ? (
          <HStack alignItems="center" space="sm" mr="$2">
            {compactSaveLabel ? (
              <Pressable onPress={compactSaveLabel === 'Error' ? onRetry : undefined}>
                <Badge size="sm" action="muted">
                  {compactSaveLabel === 'Error' && (
                    <Icon as={RefreshCw} size="2xs" color={compactSaveColor} mr="$1" />
                  )}
                  <BadgeText size="xs" color={compactSaveColor}>
                    {compactSaveLabel}
                  </BadgeText>
                </Badge>
              </Pressable>
            ) : null}
            {(targetWordcount ?? 0) > 0 ? (
              <Badge size="sm" action="muted">
                <BadgeText size="xs" color={wordcountColor}>
                  {`${wordcount?.bodyWords ?? 0}/${targetWordcount ?? 0}`}
                </BadgeText>
              </Badge>
            ) : null}
          </HStack>
        ) : null}
        {inline ? null : <Box w={1} h={24} bg="$borderLight" mr="$2" />}
        {showEvaluate ? (
          <Button
            testID="evaluate-button"
            isDisabled={Boolean(disableButtons) || !canEvaluate}
            size="xs"
            h={25}
            action="primary"
            variant="solid"
            onPress={onEvaluate}
          >
            <ButtonIcon as={Sparkles} size="sm" mr="$2" color="$textInverted" />
            <ButtonText color="$textInverted">
              {t('student.task.composer.buttons.feedback')}
            </ButtonText>
          </Button>
        ) : null}
        <SubmitButton
          show={Boolean(showSubmitButton)}
          isDisabled={Boolean(disableButtons) || !canSubmit}
          onSubmit={onSubmit}
          label={t('student.task.composer.buttons.mark-as-done')}
        />
      </HStack>
    </HStack>
  );
};

const EditorToolbar = React.memo(EditorToolbarComponent);

export default EditorToolbar;
